import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Badge, Button, Callout, Card, Eyebrow, Icon, Text, type IconName } from '../ui/index.js';
import type { AppRelease, AppReleases } from '@goodbuild/shared';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { Loading } from '../components/layout/Loading.js';
import { useAppReleases } from '../api/hooks.js';
import { openExternal } from '../auth/session.js';
import { formatBytes } from '../lib/format.js';

type PlatformKey = keyof AppReleases;

interface PlatformCard {
  key: PlatformKey;
  title: string;
  /** The title in the display face, where "Linux and Steam Deck" is two words too many. */
  short: string;
  /** What the file is, said in one line under the title. */
  format: string;
  icon: IconName;
  /** The line in the picker: who this choice is for, rather than what the file is. */
  pick: string;
  /** The line the second step opens with, under the title card. */
  heroNote: string;
  /** The heading over the numbered steps. It counts them, so it moves when they do. */
  how: string;
  /** What somebody does once the file has downloaded. */
  steps: readonly string[];
  /** The line under the steps, for the part of this that is not a step. */
  after: string;
  /**
   * On the roadmap rather than out. The second step explains that instead of offering a file.
   *
   * A release that does turn up is still honoured, so publishing one is all it takes to undo
   * this: the flag decides what an empty platform says, not whether a built one is offered.
   */
  planned?: boolean;
}

const PLATFORMS: PlatformCard[] = [
  {
    key: 'windows',
    title: 'Windows',
    short: 'Windows',
    format: 'Installer · Windows 10 and 11',
    icon: 'windows',
    pick: 'A desktop or a laptop running Windows 10 or 11.',
    heroNote: 'Windows 10 and 11. One installer, and the app keeps itself up to date after that.',
    how: 'Three steps, and it is on the desktop.',
    steps: [
      'Run the installer once it has downloaded. It is signed by us, and it installs for you alone, so Windows never asks for an administrator.',
      'Open GOOD BUILD from the Start menu.',
      'Sign in once. The library, and every build you can play, is on the other side of that.',
    ],
    after:
      'From then on it updates itself in the background. This is the last installer you download.',
  },
  {
    key: 'linux',
    title: 'Linux and Steam Deck',
    short: 'Linux',
    format: 'AppImage · x86_64',
    icon: 'linux',
    pick: 'A Steam Deck, or any x86_64 Linux machine.',
    heroNote: 'An AppImage that runs anywhere, and the same file you add to Steam on a Steam Deck.',
    how: 'Four steps, and it is in your Steam library.',
    // The Steam Deck setup guide (`03-client-app.md` section 6), which is why these four and in
    // this order. The in-app help page shows the same list.
    steps: [
      'Switch the Deck to Desktop Mode. On a desktop Linux machine there is nothing to switch.',
      'Move the AppImage somewhere it can stay, like ~/Applications, and make it executable — right-click, Properties, Permissions, or chmod +x on it.',
      'Open Steam, choose "Add a Non-Steam Game", and pick the AppImage.',
      'Back to Gaming Mode. GOOD BUILD is in your library, and it opens with the controller.',
    ],
    after:
      'On a desktop only the second step matters: the AppImage runs on a double-click, and updates itself after that.',
  },
  {
    key: 'android',
    title: 'Android',
    short: 'Android',
    format: 'APK · direct from us',
    icon: 'android',
    pick: 'A phone or a tablet running Android.',
    heroNote: 'Straight from us, never from a store. The app verifies the file before it installs.',
    how: 'Three taps, and it is on the phone.',
    steps: [
      'Keep the file. The browser asks about anything that did not come from a store, and this did not come from a store.',
      'Open it. Android asks once whether this browser may install apps — allow it, and it does not ask again.',
      'Install, then open GOOD BUILD.',
    ],
    after:
      'Every update after this one comes from inside the app, which checks each APK against its published hash before handing it to Android.',
    planned: true,
  },
];

/** The console pitch. Three beats, because a fourth would be a feature list. */
const PILLARS: Array<{ icon: IconName; title: string; body: string }> = [
  {
    icon: 'gamepad',
    title: 'Made for a controller',
    body: 'Every screen is laid out for a stick and four buttons. A is select, B is back, Y is search, and the hints bar always says what the button under your thumb does right now.',
  },
  {
    icon: 'tv',
    title: 'Built for the couch',
    body: 'Type, targets and focus scale up for a television across a room. Artwork fills the screen and the library reads from the sofa, not from eighteen inches away.',
  },
  {
    icon: 'shield',
    title: 'Patches, not re-downloads',
    body: 'A new build only pulls down the chunks that changed. Lose power mid-update and the game either launches or repairs itself — it is never left half written.',
  },
];

const STEP_LABELS = ['Choose your platform', 'Download and install'] as const;

function isPlatformKey(value: unknown): value is PlatformKey {
  return PLATFORMS.some((platform) => platform.key === value);
}

export const Route = createFileRoute('/download')({
  component: Download,
  /**
   * Which step this is, kept in the URL rather than in a `useState`.
   *
   * It has to be the URL for two reasons. B and Escape go back through history everywhere in
   * the app (`03-client-app.md` section 3.3), so the second step has to be a history entry or
   * the button that means "back" would leave the page instead of returning to the picker. And a
   * link to the instructions for one platform is the thing worth sending somebody who asks how
   * to install it.
   */
  validateSearch: (search: Record<string, unknown>): { platform?: PlatformKey } =>
    isPlatformKey(search.platform) ? { platform: search.platform } : {},
});

/**
 * Which platform this visitor is most likely on.
 *
 * The guess no longer chooses for anybody, now that choosing is the first step. It marks one of
 * the three as theirs and starts the focus there, so a controller is one press from the right
 * one and a mouse still has all three in front of it.
 */
function guessPlatform(): PlatformKey {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/windows|win32|win64/i.test(ua)) return 'windows';
  if (/linux|cros|x11/i.test(ua)) return 'linux';
  // A Mac or an iPhone has nothing to download, so none of the three is marked as theirs and the
  // picker is simply three choices. Focus starts on Windows, because most people are there.
  return 'windows';
}

/**
 * Where the apps come from (`03-client-app.md` section 6).
 *
 * This is the one screen a visitor reaches before the app exists for them, and it is two steps:
 * say which machine you are on, then get the file and the instructions for that machine and
 * nothing else. Three sets of instructions on one page is how somebody on a Deck ends up
 * reading the Windows ones.
 *
 * Each button still points straight at an installer. The URLs come from `GET /v1/app/releases`,
 * which reads them out of the two release manifests: those manifests are what the updaters poll
 * and are not themselves downloads, and the API is the only side that knows which environment's
 * files origin to use.
 *
 * The Android APK is only distributed from here, never from a store
 * (`01-product-rules.md` section 10).
 */
function Download() {
  const releases = useAppReleases();
  const navigate = useNavigate();
  const { platform: chosenKey } = Route.useSearch();
  const [guess] = React.useState(guessPlatform);

  if (releases.isLoading) return <Loading label="Finding the latest build" />;

  const data = releases.data ?? null;
  const nothingYet = data ? !data.windows && !data.linux && !data.android : true;
  const chosen = PLATFORMS.find((platform) => platform.key === chosenKey) ?? null;

  // Both steps say the same thing when the list cannot be read or is empty, and they say it in
  // the same place: under the opening shot, before anything asks to be pressed.
  const problem = releases.isError ? (
    <Callout.Root color="danger">
      <Callout.Description>
        {'We could not reach the download list. Try again in a moment.'}
      </Callout.Description>
    </Callout.Root>
  ) : nothingYet ? (
    <Callout.Root color="amber">
      <Callout.Description>
        {'No build has been published yet. This page fills in with the first release.'}
      </Callout.Description>
    </Callout.Root>
  ) : null;

  if (chosen) {
    return (
      <InstallStep
        platform={chosen}
        release={data?.[chosen.key] ?? null}
        problem={problem}
        // Replaced rather than pushed: this is the way back out of a choice, so it should undo
        // the entry that choice made instead of stacking a third one on top of it.
        onBack={() => void navigate({ to: '/download', search: {}, replace: true })}
      />
    );
  }

  return (
    <ChooseStep
      releases={data}
      guess={guess}
      problem={problem}
      onChoose={(key) => void navigate({ to: '/download', search: { platform: key } })}
    />
  );
}

/** Which of the two steps this is. Read, never pressed: the choices themselves are the way on. */
function StepBar({ current }: { current: 1 | 2 }) {
  return (
    <ol className="gb-stepbar" aria-label={`Step ${current} of ${STEP_LABELS.length}`}>
      {STEP_LABELS.map((label, index) => {
        const number = index + 1;
        const state = number === current ? 'current' : number < current ? 'done' : 'todo';
        return (
          <li
            key={label}
            className="gb-stepbar__item"
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className="gb-stepbar__number" aria-hidden="true">
              {state === 'done' ? <Icon name="check" size={13} /> : String(number)}
            </span>
            <span className="gb-stepbar__label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Step one: the opening shot, then the three machines.
 *
 * The pitch sits under the picker rather than over it, because somebody who came here to install
 * has already been sold, and somebody who has not can keep reading.
 */
function ChooseStep({
  releases,
  guess,
  problem,
  onChoose,
}: {
  releases: AppReleases | null;
  guess: PlatformKey;
  problem: React.ReactNode;
  onChoose: (key: PlatformKey) => void;
}) {
  return (
    <FocusZone focusKey="download" direction="column" className="gb-stack gb-stack--loose">
      {/* ------------------------------------------------------------- hero */}
      <section className="gb-cine">
        {/* The room, drawn rather than photographed: a warm wash, the stripe at 45 degrees,
            and a vignette that pulls the corners down so the type sits forward of it. */}
        <span className="gb-cine__wash" aria-hidden="true" />
        <span className="gb-cine__rays" aria-hidden="true" />
        <span className="gb-cine__vignette" aria-hidden="true" />

        <div className="gb-cine__copy">
          <Eyebrow tone="lemon">{'GOOD BUILD OS'}</Eyebrow>

          <h1 className="gb-cine__title">
            {'Put the'}
            <br />
            {'workshop'}
            <br />
            {'on your screen.'}
          </h1>

          <Text render={<p />} className="gb-cine__sub">
            {
              'One app for every game we are making. Install it, and the build we pushed this morning is a button press away — on a desk, on a television, or on a Steam Deck.'
            }
          </Text>

          <Text size="2" className="gb-cine__note">
            {'Step 1 of 2 · Tell us what you are installing on'}
          </Text>

          <Text render={<p />} size="1" className="gb-cine__foot">
            {'Free to install. Browsing is free. A membership is what opens the Play button.'}
          </Text>
        </div>
      </section>

      {problem}

      {/* -------------------------------------------------------- the picker */}
      <section className="gb-section">
        <StepBar current={1} />
        <Eyebrow>{'Where are you installing it?'}</Eyebrow>
        <div className="gb-platforms">
          {PLATFORMS.map((platform) => (
            <PlatformChoice
              key={platform.key}
              platform={platform}
              release={releases?.[platform.key] ?? null}
              current={platform.key === guess}
              onChoose={onChoose}
            />
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------- the pitch */}
      <section className="gb-section">
        <Eyebrow>{'Why the app'}</Eyebrow>
        <div className="gb-grid gb-grid--wide">
          {PILLARS.map((pillar) => (
            <Card key={pillar.title} size="3" className="gb-pillar">
              <span className="gb-pillar__icon" aria-hidden="true">
                <Icon name={pillar.icon} size={22} />
              </span>
              <h3 className="gb-display gb-display--6">{pillar.title}</h3>
              <Text render={<p />} size="2" color="gray">
                {pillar.body}
              </Text>
            </Card>
          ))}
        </div>
      </section>
    </FocusZone>
  );
}

/**
 * One machine to choose, as a panel rather than a card.
 *
 * The whole panel is the target rather than a button inside it: at this step there is one thing
 * to do with a platform, so anywhere on it should do that. The glyph is the size the board draws
 * a mark at, which is what makes three of these in a row read as the back of a box.
 */
function PlatformChoice({
  platform,
  release,
  current,
  onChoose,
}: {
  platform: PlatformCard;
  release: AppRelease | null;
  current: boolean;
  onChoose: (key: PlatformKey) => void;
}) {
  const planned = !release && platform.planned;

  return (
    <Focusable
      autoFocus={current}
      onSelect={() => onChoose(platform.key)}
      selectLabel={`Set up ${platform.title}`}
    >
      <article className="gb-platform" data-current={current}>
        <span className="gb-platform__glyph" aria-hidden="true">
          <Icon name={platform.icon} size={34} />
        </span>

        <div className="gb-platform__head">
          <h3 className="gb-display gb-display--6">{platform.title}</h3>
          {release ? <Badge color="gray">{release.version}</Badge> : null}
          {planned ? <Badge color="lemon">{'Planned'}</Badge> : null}
          {current ? <Badge color="green">{'Your device'}</Badge> : null}
        </div>

        <Text render={<p />} size="2" color="gray">
          {platform.pick}
        </Text>

        {/* What A does, said at the floor of the panel. `.gb-platform > :last-child` pushes it
            down, so three panels of different text lengths still line these up. */}
        <span className="gb-platform__go">
          <Text size="2">
            {release ? 'Set it up' : planned ? 'See what is coming' : 'See the steps'}
          </Text>
          <Icon name="chevron-right" size={16} />
        </span>
      </article>
    </Focusable>
  );
}

/**
 * Step two: the file for the machine they picked, then what to do with it.
 *
 * The download is the title card's own action rather than a link in a list, because by this
 * point there is exactly one file on this page and pressing A should fetch it.
 */
function InstallStep({
  platform,
  release,
  problem,
  onBack,
}: {
  platform: PlatformCard;
  release: AppRelease | null;
  problem: React.ReactNode;
  onBack: () => void;
}) {
  const planned = !release && platform.planned;

  return (
    <FocusZone focusKey="download" direction="column" className="gb-stack gb-stack--loose">
      {/* ------------------------------------------------------------- hero */}
      <section className="gb-cine">
        <span className="gb-cine__wash" aria-hidden="true" />
        <span className="gb-cine__rays" aria-hidden="true" />
        <span className="gb-cine__vignette" aria-hidden="true" />

        <div className="gb-cine__copy">
          <Eyebrow tone="lemon">
            {release ? `GOOD BUILD OS / Build ${release.version}` : 'GOOD BUILD OS'}
          </Eyebrow>

          <h1 className="gb-cine__title">
            {'Install it'}
            <br />
            {`on ${platform.short}.`}
          </h1>

          <Text render={<p />} className="gb-cine__sub">
            {platform.heroNote}
          </Text>

          <div className="gb-cine__actions">
            {release ? (
              <Focusable
                autoFocus
                focusKey="download-primary"
                onSelect={() => void openExternal(release.url)}
                selectLabel={`Download for ${platform.title}`}
                className="gb-focusable--flat"
              >
                <Button size="4">
                  <Icon name="download" size={16} />
                  {`Download for ${platform.title}`}
                </Button>
              </Focusable>
            ) : (
              <Button size="4" disabled>
                {planned ? `${platform.title} is coming` : 'Not released yet'}
              </Button>
            )}

            <Text size="2" className="gb-cine__note">
              {release
                ? `${platform.format}${release.sizeBytes ? ` · ${formatBytes(release.sizeBytes)}` : ''}`
                : platform.format}
            </Text>
          </div>

          <Text render={<p />} size="1" className="gb-cine__foot">
            {'Free to install. Browsing is free. A membership is what opens the Play button.'}
          </Text>
        </div>
      </section>

      {/* The step rail, with the way back to the other two next to it. B and Escape do the same
          thing, because the step is a history entry. */}
      <div className="gb-row gb-row--between">
        <StepBar current={2} />
        <Focusable
          autoFocus={!release}
          onSelect={onBack}
          selectLabel="Choose a different platform"
          className="gb-focusable--flat"
        >
          <Button size="2" variant="surface">
            <Icon name="chevron-left" size={14} />
            {'Choose a different platform'}
          </Button>
        </Focusable>
      </div>

      {problem}

      {planned && !release ? (
        <Callout.Root color="amber">
          <Callout.Description>
            {`${platform.title} is not out yet. These are the steps for when it is, and this page turns into the download the day it ships.`}
          </Callout.Description>
        </Callout.Root>
      ) : null}

      {/* -------------------------------------------------- what to do next */}
      <section className="gb-section">
        <Eyebrow>{'Then do this'}</Eyebrow>
        <Card size="4" className="gb-stack">
          <h3 className="gb-display gb-display--5" style={{ maxWidth: '24ch' }}>
            {platform.how}
          </h3>
          <ol className="gb-steps">
            {platform.steps.map((step, index) => (
              <li key={step}>
                <span className="gb-steps__number" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <Text size="3">{step}</Text>
              </li>
            ))}
          </ol>
          <Text render={<p />} size="2" color="gray">
            {platform.after}
          </Text>
        </Card>
      </section>

      {/* The Android installer checks this hash before handing the APK to PackageInstaller, so a
          swapped file is refused on the device. It is printed so it can be checked by hand too,
          and only on the step that is actually about the APK. */}
      {platform.key === 'android' && release?.sha256 ? (
        <Callout.Root>
          <Callout.Description>
            <Text size="2">
              {'The APK checks out to SHA-256 '}
              <Text size="2" mono style={{ wordBreak: 'break-all' }}>
                {release.sha256}
              </Text>
              {'. The app verifies this before installing.'}
            </Text>
          </Callout.Description>
        </Callout.Root>
      ) : null}
    </FocusZone>
  );
}
